import { distance, dot, magnitude, subtract } from "./vector";

interface CircleBody {
  readonly x: number;
  readonly y: number;
  readonly vx: number;
  readonly vy: number;
  readonly radius: number;
  readonly mass: number;
}

export function detectCollision(a: CircleBody, b: CircleBody): boolean {
  return distance({ x: a.x, y: a.y }, { x: b.x, y: b.y }) < a.radius + b.radius;
}

export function resolveCollision(
  a: CircleBody,
  b: CircleBody,
  restitution: number,
): {
  readonly a: { readonly vx: number; readonly vy: number };
  readonly b: { readonly vx: number; readonly vy: number };
} {
  const normal = subtract({ x: b.x, y: b.y }, { x: a.x, y: a.y });
  const dist = magnitude(normal);

  if (dist === 0) {
    return { a: { vx: a.vx, vy: a.vy }, b: { vx: b.vx, vy: b.vy } };
  }

  const nx = normal.x / dist;
  const ny = normal.y / dist;
  const relativeVelocity = subtract({ x: a.vx, y: a.vy }, { x: b.vx, y: b.vy });
  const relativeSpeed = dot(relativeVelocity, { x: nx, y: ny });

  if (relativeSpeed <= 0) {
    return { a: { vx: a.vx, vy: a.vy }, b: { vx: b.vx, vy: b.vy } };
  }

  const impulse = ((1 + restitution) * relativeSpeed) / (a.mass + b.mass);

  return {
    a: {
      vx: a.vx - impulse * b.mass * nx,
      vy: a.vy - impulse * b.mass * ny,
    },
    b: {
      vx: b.vx + impulse * a.mass * nx,
      vy: b.vy + impulse * a.mass * ny,
    },
  };
}

export function separateOverlap(
  a: CircleBody,
  b: CircleBody,
): {
  readonly a: { readonly x: number; readonly y: number };
  readonly b: { readonly x: number; readonly y: number };
} {
  const normal = subtract({ x: b.x, y: b.y }, { x: a.x, y: a.y });
  const dist = magnitude(normal);
  const minDist = a.radius + b.radius;

  if (dist >= minDist || dist === 0) {
    return { a: { x: a.x, y: a.y }, b: { x: b.x, y: b.y } };
  }

  const overlap = minDist - dist;
  const nx = normal.x / dist;
  const ny = normal.y / dist;
  const totalMass = a.mass + b.mass;
  const aRatio = b.mass / totalMass;
  const bRatio = a.mass / totalMass;

  return {
    a: {
      x: a.x - nx * overlap * aRatio,
      y: a.y - ny * overlap * aRatio,
    },
    b: {
      x: b.x + nx * overlap * bRatio,
      y: b.y + ny * overlap * bRatio,
    },
  };
}
